import { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import { applyPanelLanguage } from "../../translate/uiLanguage";
import api, { cancelAllPendingRequests, resetLogoutFlag } from "../../services/api";
import toastError from "../../errors/toastError";
import axios from "axios";
import { SocketContext } from "../../context/Socket/SocketContext";
import moment from "moment";

/** Mantiene alineados token refresh y SocketManager (evita companyId null / obsoleto en localStorage). */
const syncLocalSessionKeysFromUser = u => {
  if (!u || typeof u !== "object") return;
  if (u.companyId != null && u.companyId !== "") {
    localStorage.setItem("companyId", String(u.companyId));
  }
  if (u.id != null && u.id !== "") {
    localStorage.setItem("userId", String(u.id));
  }
};

const notifySubscriptionDue = u => {
  const dueDate = u?.company?.dueDate;
  const companyId = u?.companyId || u?.company?.id;
  if (!dueDate || !companyId) return;

  const due = moment(dueDate).endOf("day");
  if (!due.isValid()) return;

  const daysLeft = Math.ceil(due.diff(moment(), "days", true));
  if (daysLeft < 0 || daysLeft > 5) return;

  const today = moment().format("YYYY-MM-DD");
  const dueKey = due.format("YYYY-MM-DD");
  const storageKey = `subscriptionDueAlert:${companyId}:${dueKey}`;
  if (localStorage.getItem(storageKey) === today) return;

  const message = daysLeft === 0
    ? "Tu plan vence hoy."
    : `Tu plan vence en ${daysLeft} ${daysLeft === 1 ? "día" : "días"}.`;

  toast.warn(message, { autoClose: 12000 });
  localStorage.setItem(storageKey, today);
};

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        // ✅ NO establecer isAuth aquí, solo en useEffect inicial
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // ✅ FIX 03/12/2025: Manejar tanto 403 como 401 con refresh token
      // Antes solo se manejaba 403, ahora también 401 (token expirado)
      const isAuthError = error?.response?.status === 403 || error?.response?.status === 401;
      
      if (isAuthError && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          try {
            const { data } = await api.post("/auth/refresh_token", { refreshToken });
            if (data) {
              localStorage.setItem("token", data.token);
              if (data.refreshToken) {
                localStorage.setItem("refreshToken", data.refreshToken);
              }
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
              syncLocalSessionKeysFromUser(data.user);
              if (data.user) setUser(data.user);
              console.log(`✅ Token renovado después de error ${error?.response?.status}`);
            }
            return api(originalRequest);
          } catch (refreshError) {
            // ✅ SI FALLA EL REFRESH, LIMPIAR TODO
            console.warn("❌ Error renovando token, cerrando sesión");
            localStorage.removeItem("token");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("companyId");
            localStorage.removeItem("userId");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            return Promise.reject(refreshError);
          }
        } else {
          // ✅ No hay refresh token, limpiar sesión
          console.warn("❌ No hay refresh token disponible, cerrando sesión");
        localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
        localStorage.removeItem("companyId");
        localStorage.removeItem("userId");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        }
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );

  const socketManager = useContext(SocketContext);

  const mergeSessionUser = useCallback((partial) => {
    if (!partial || typeof partial !== "object") return;
    setUser((prev) => ({ ...prev, ...partial }));
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    applyPanelLanguage(user.language);
  }, [user?.id, user?.language]);

  useEffect(() => {
    if (!user?.id) return;
    notifySubscriptionDue(user);
  }, [user?.id, user?.companyId, user?.company?.dueDate]);

  // ✅ FIX 03/12/2025: Función para verificar y renovar token proactivamente
  const checkAndRefreshToken = async () => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    
    if (!token || !refreshToken) return false;
    
    try {
      // Decodificar token para obtener exp
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      const exp = payload.exp;
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = exp - currentTime;
      
      // ✅ FIX 12/03/2025: Renovar solo si falta menos de 2 horas (7200 segundos) O si ya expiró
      // Esto evita renovaciones demasiado frecuentes (antes era 1 hora)
      if (timeUntilExpiry < 7200) {
        console.log(`🔄 Token ${timeUntilExpiry <= 0 ? 'expirado' : 'por expirar en ' + Math.round(timeUntilExpiry/60) + ' minutos'}, renovando proactivamente...`);
        
        const { data } = await api.post("/auth/refresh_token", { refreshToken });
        localStorage.setItem("token", data.token);
        if (data.refreshToken) {
          localStorage.setItem("refreshToken", data.refreshToken);
        }
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        syncLocalSessionKeysFromUser(data.user);
        if (data.user) setUser(data.user);
        console.log("✅ Token renovado proactivamente");
        return true;
      }
      return false;
    } catch (err) {
      console.error("❌ Error renovando token proactivamente:", err);
      return false;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken"); // ✅ OBTENER REFRESH TOKEN
    (async () => {
      if (token && refreshToken) { // ✅ VERIFICAR QUE AMBOS EXISTAN
        try {
          const { data } = await api.post("/auth/refresh_token", { refreshToken }); // ✅ ENVIAR REFRESH TOKEN
          localStorage.setItem("token", data.token); // ✅ ACTUALIZAR TOKEN
          if (data.refreshToken) {
            localStorage.setItem("refreshToken", data.refreshToken); // ✅ ACTUALIZAR REFRESH TOKEN SI SE PROPORCIONA
          }
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          syncLocalSessionKeysFromUser(data.user);
          setIsAuth(true);
          setUser(data.user);
        } catch (err) {
          // ✅ SI FALLA EL REFRESH, LIMPIAR TODO Y REDIRIGIR AL LOGIN
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("companyId");
          localStorage.removeItem("userId");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // ✅ FIX 12/03/2025: Verificación periódica para renovar token antes de que expire
  useEffect(() => {
    // ✅ Verificar cada 1 hora si el token está por expirar (antes era cada 30 minutos)
    // Esto reduce la frecuencia de verificaciones y renovaciones innecesarias
    const interval = setInterval(async () => {
      const token = localStorage.getItem("token");
      if (token && isAuth) {
        await checkAndRefreshToken();
      }
    }, 60 * 60 * 1000); // Cada 1 hora (antes era 30 minutos)
    
    return () => clearInterval(interval);
  }, [isAuth]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (companyId) {
   
      const socket = socketManager.getSocket(companyId);

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user) {
          setUser((prev) => {
            if (!prev?.id || prev.id !== data.user.id) return prev;
            // Merge: el payload del socket (p. ej. tras cambiar idioma) no siempre trae todos los campos;
            // reemplazar el objeto entero borraba `super` y ocultaba ítems del menú hasta recargar.
            return { ...prev, ...data.user };
          });
        }
      });

      socket.on(`company-${companyId}-payment`, (data) => {
        if (data?.action !== "payment-reminder") {
          return;
        }

        const currency = data.currency || "USD";
        const value = data.valueFormatted || Number(data.value || 0).toLocaleString(currency === "COP" ? "es-CO" : "en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: currency === "COP" ? 0 : 2,
          maximumFractionDigits: currency === "COP" ? 0 : 2
        });
        const dueDate = data.dueDate ? ` Vencimiento: ${data.dueDate}.` : "";
        toast.warn(`${data.message} Plan: ${data.detail || "actual"}. Valor: ${value}.${dueDate}`, {
          autoClose: 12000
        });
      });
    
    
    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-user`);
        socket.off(`company-${companyId}-payment`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
    };
  }
  }, [socketManager, user]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { companyId, id, company },
      } = data;

      if (has(company, "settings") && isArray(company.settings)) {
        const setting = company.settings.find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      applyPanelLanguage(data.user?.language);
      const dueDate = data.user?.company?.dueDate;
      const isSuper = data.user?.super === true;
      const dueMoment = dueDate ? moment(dueDate) : null;
      const planIsActive =
        isSuper ||
        !dueMoment ||
        !dueMoment.isValid() ||
        moment().isSameOrBefore(dueMoment.endOf("day"));

      if (planIsActive) {
      localStorage.setItem("token", data.token);
      localStorage.setItem("refreshToken", data.refreshToken); // ✅ GUARDAR REFRESH TOKEN
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);
      if (dueMoment && dueMoment.isValid()) {
        localStorage.setItem("companyDueDate", dueMoment.format("DD/MM/yyyy"));
      }
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);
      // ✅ CRÍTICO: Resetear flag de logout después de login exitoso
      resetLogoutFlag();
      toast.success(i18n.t("auth.toasts.success"));
        history.push("/tickets");
        setLoading(false);
      } else {
        toastError(`Tu plan venció el ${dueMoment.format("DD/MM/yyyy")}. Contacta a soporte para más información.`);
        setLoading(false);
      }

      //quebra linha 
    } catch (err) {
      setLoading(false);
      if (err?.response?.data?.error === "ERR_EMAIL_NOT_VERIFIED") {
        throw err;
      }
      toastError(err);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // ✅ CRÍTICO: Cancelar todas las peticiones pendientes ANTES de hacer logout
      // Esto evita múltiples warnings de "Invalid token" en el backend
      cancelAllPendingRequests();
      
      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken"); // ✅ LIMPIAR REFRESH TOKEN
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      // ✅ Si el error es por cancelación, no mostrar error al usuario
      if (axios.isCancel && axios.isCancel(err)) {
        console.log("✅ Peticiones canceladas durante logout");
      } else {
        toastError(err);
      }
      setIsAuth(false);
      setUser({});
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("companyId");
      localStorage.removeItem("userId");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data.user; // ✅ CORREGIDO: Devolver data.user en lugar de data
    } catch (err) {
      toastError(err);
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    mergeSessionUser,
    socket: socketManager.getSocket(localStorage.getItem("companyId")),
  };
};

export default useAuth;
